import React, { useState, useEffect } from "react";

const UploadFile = ({
  id,
  label,
  placeholder,
  register,
  errors,
  watch,
  disabled,
  className,
  emailclass,
  tooltipText,
  value = "",
  classTick,
  maxLength,
  trigger,
  onChange = () => {},
  onInput = () => {},
  liveValidate = true,
  ...props
}) => {
  const [files, setFiles] = useState([]); // Track multiple files
  const [isUploading, setIsUploading] = useState(false); // Overall upload state
  const [uploadError, setUploadError] = useState(null); // Upload error state
  const [isDragOver, setIsDragOver] = useState(false); // Drag-over state
  const maxFiles = 5; // Set the maximum number of files

  useEffect(() => {
    if (watch && liveValidate) {
      const trig = async () => await trigger(id);
      trig();
    }
  }, [watch]);

  const handleFileChange = (e) => {
    const selectedFiles = e.target.files || e.dataTransfer.files;
    if (selectedFiles) {
      // If the number of files already uploaded plus the new files exceeds the maxFiles limit
      if (files.length + selectedFiles.length > maxFiles) {
        setUploadError(`You can only upload up to ${maxFiles} files.`);
        return;
      }

      setUploadError(null); // Clear any existing error
      const newFiles = Array.from(selectedFiles).map((file) => ({
        file,
        progress: 0,
        isUploading: true,
      }));
      setFiles((prevFiles) => [...prevFiles, ...newFiles]);
      newFiles.forEach((fileObj) => simulateFileUpload(fileObj));
    }
  };

  const simulateFileUpload = (fileObj) => {
    let progress = 0;
    const interval = setInterval(() => {
      if (progress < 100) {
        progress += 10;
        setFiles((prevFiles) =>
          prevFiles.map((f) =>
            f.file.name === fileObj.file.name ? { ...f, progress } : f
          )
        );
      } else {
        clearInterval(interval);
        setFiles((prevFiles) =>
          prevFiles.map((f) =>
            f.file.name === fileObj.file.name
              ? { ...f, progress: 100, isUploading: false }
              : f
          )
        );
      }
    }, 500);
  };

  const cancelUpload = (fileName) => {
    setFiles((prevFiles) =>
      prevFiles.filter((file) => file.file.name !== fileName)
    );
  };

  const clearFile = (fileName) => {
    setFiles((prevFiles) =>
      prevFiles.filter((file) => file.file.name !== fileName)
    );
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    handleFileChange(e);
  };

  return (
    <div className={`licField ${className}`}>
      {label && (
        <label className="labelName" htmlFor={id}>
          {label}
        </label>
      )}

      {/* Drag and Drop Area */}

      {files.length < maxFiles && (
        <>
          <div
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            onDragLeave={handleDragLeave}
            className={`upload-container margin-bottom-15`}
          >
            {isDragOver && (
              <div className="drag-over">Drop files here to upload…</div>
            )}
            <div className="upload-placeholder">
              {placeholder || "Drop files here to upload…"}
            </div>
            <input
              type="file"
              id="uploadFiles"
              onChange={handleFileChange}
              disabled={disabled || files.length >= maxFiles}
              className="file-input"
              hidden
              multiple // Allow multiple files to be selected
            />
            <label htmlFor="uploadFiles" className="upload-btn">
              Browse Files
            </label>
          </div>
          {uploadError && <span className="errorField">{uploadError}</span>}
        </>
      )}
      {/* File upload progress and file names above the drag and drop area */}
      <div className="uploading-files-list margin-bottom-15">
        {files.map((fileObj) => (
          <div key={fileObj.file.name} className="uploaded-container">
            <div>{fileObj.file.name}</div>
            {fileObj.isUploading && (
              <>
                <div className="progress-bar">
                  <div
                    className="progress-bar-inner"
                    style={{ width: `${fileObj.progress}%` }}
                  />
                </div>
                <div className="progress-text">{`${Math.round(
                  fileObj.progress
                )}% complete`}</div>
              </>
            )}
            <button
              className="upload-btn"
              onClick={() => cancelUpload(fileObj.file.name)}
            >
              {fileObj.isUploading ? "Cancel" : "Clear"}
            </button>
          </div>
        ))}
      </div>
      {files.length >= maxFiles && (
        <div className="errorField margin-top-10 margin-bottom-10">
          Max file limit reached.
        </div>
      )}
    </div>
  );
};

export default UploadFile;
